<template>
  <b-layout :colors="$site.colors" :header-title="title" :header-back-route="homeRoute">
    <b-container class="mt-12">
      <div class="max-w-lg mx-auto">
        <voucher-alert />

        <div v-if="error" class="error">
          {{ error }}
        </div>
        <div v-else-if="timesLoading" class="loading">
          <b-card
            :title="$t('general.loading')"
            class="mb-6"
          >
            <div class="text-center">
              <b-spinner label="Loading" class="mx-auto" />
            </div>
          </b-card>
        </div>
        <div v-else-if="lastMinute">
          <template v-for="date in lastMinute.dates">
            <b-card
              v-if="date.bookable"
              :key="date.date"
              :title="$format.date(date.date)"
              class="mb-6"
            >
              <p v-if="!date.times.length" class="text-sm text-secondary/70 text-center">
                {{ $t('general.nothingToday') }}
              </p>

              <ul v-else>
                <li v-for="time in date.times" :key="time.label">
                  <a class="w-full bg-action/20 text-primary p-2 rounded block mb-2 text-center cursor-pointer hover:bg-action hover:text-action transition-all" @click="timeSelected(date, time)">
                    {{ time.label }} <span v-if="time.multipleTimes" class="text-sm">- {{ $t('general.variousAvailable') }}</span>
                  </a>

                  <div v-if="selectedDate === date && selectedTime === time" class="mt-2 p-3 rounded border bg-secondary mb-5">
                    <div class="flex items-center space-x-6  rtl:space-x-reverse mb-2">
                      <a v-if="selectedService" class="text-sm flex items-center" @click="selectedService = null">
                        <b-icon icon="chevronLeft" class="mr-1 h-4 w-4" /> {{ $t('general.back') }}
                      </a>

                      <h4 class="font-semibold text-secondary">
                        {{ cardTitle(time, selectedService) }}
                      </h4>
                    </div>

                    <ul v-if="!selectedService" class="rounded overflow-hidden">
                      <li v-for="service in time.services" :key="service.id" class="mb-px">
                        <a class="text-sm flex items-center justify-between w-full bg-action/20 text-primary p-2 cursor-pointer hover:bg-action hover:text-action transition-all" @click="serviceSelected(date, time, service)">
                          {{ service.title }}

                          <b-icon icon="chevronRight" />
                        </a>
                      </li>
                    </ul>

                    <div v-if="selectedDate === date && selectedTime === time && selectedService">
                      <hr class="bookapp-action-hr w-100">

                      <div v-if="availabilityLoading" class="loading">
                        <div class="text-center">
                          <b-spinner label="Loading" class="m-3" />
                        </div>
                      </div>

                      <ul v-if="!availabilityLoading && availability" class="list-inline mt-2">
                        <li v-for="slot in availableTimes" :key="slot.data">
                          <b-button v-b-tooltip="slotTooltip(slot)" variant="action" size="sm" class="m-1" @click="slotClicked(slot)">
                            {{ slot.startTimeLabel }}
                          </b-button>
                        </li>
                      </ul>
                    </div>
                  </div>
                </li>
              </ul>
            </b-card>
          </template>
        </div>
      </div>
    </b-container>
  </b-layout>
</template>

<script>
import { BookingStage, createBookingRoute } from '@/components/booking/elements/Detail.vue'
import VoucherAlert from '@/components/voucher/elements/Alert.vue'

definePageMeta({
  tabs: ['lastMinute'],
  middleware: ['redirects']
});

export default defineNuxtComponent({
  components: {
    VoucherAlert
  },
  layout: 'default',
  data () {
    return {
      timesLoading: false,
      error: null,
      lastMinute: null,
      selectedDate: null,
      selectedTime: null,
      selectedService: null,
      availabilityLoading: false,
      availability: null
    }
  },
  async asyncData () {
    useTabMeta('lastMinute')

    const route = useRoute()
    const locationStore = useLocationStore()
    const bookingStore = useBookingStore()

    try { 
      await locationStore.getServices()
      await bookingStore.setFromUrlQuery(route.query)
    } catch (e) {
      error.value = e?.errors
    }

    return {}
  },
  computed: {
    title () {
      return this.$site.tabs.lastMinute.name
    },
    info () {
      return this.$site.tabs.getInfo('lastMinute')
    },
    homeRoute () {
      return this.$store.getters.homeRoute || { path: '/' }
    },
    availableTimes () {
      const offered = []
      return this.availability.slots.filter((slot) => {
        if (offered.includes(slot.slot)) { return false }

        offered.push(slot.slot)
        return true
      })
    },
    availableMorningTimes () {
      const offered = []
      return this.availability.slots.filter((slot) => {
        if (!(slot.startTime < 720) || offered.includes(slot.slot)) { return false }

        offered.push(slot.slot)
        return true
      })
    },
    availableAfternoonTimes () {
      const offered = []
      return this.availability.slots.filter((slot) => {
        if (!(slot.startTime >= 720) || offered.includes(slot.startTime)) { return false }

        offered.push(slot.startTime)
        return true
      })
    },
    voucher () {
      return this.$store.getters['booking/voucher']
    }
  },
  watch: {
    voucher () {
      this.fetchLastMinute()
    }
  },
  created () {
    if (this.initialLastMinute) { this.lastMinute = this.initialLastMinute }

    if (!this.lastMinute) { this.fetchLastMinute() }
  },
  methods: {
    cardTitle (time, selectedService) {
      let title = ''
      if (selectedService) {
        title = title + selectedService.title + ': '
      }

      return title + (time.multipleTimes ? this.$t('pages.lastMinute.availableFrom', { time: time.label }) : this.$t('pages.lastMinute.availableAt', { time: time.label }))
    },
    slotTooltip (slot) {
      let tooltip = ''
      for (let i = 0; i < slot.services.length; i++) {
        const slotService = slot.services[i]

        tooltip += slotService.service.title + ' with ' + slotService.employee.firstName
      }

      if (this.$site.settings.bookShowServicePrices && slot.totalPrice > 0) {
        tooltip += ': ' + slot.totalPriceLabel
      }

      return tooltip
    },
    async fetchLastMinute () {
      this.timesLoading = true

      const response = await this.$api.booking.lastMinute((this.voucher && this.voucher.isValid) ? this.voucher : null)
      this.timesLoading = false

      this.lastMinute = response
    },
    timeSelected (date, time) {
      this.selectedDate = date
      this.selectedTime = time
      this.selectedService = null
      this.availability = null

      if (time && time.services.length === 1) {
        this.serviceSelected(date, time, time.services[0])
      }
    },
    serviceSelected (date, time, service) {
      this.selectedDate = date
      this.selectedTime = time
      this.selectedService = service
      this.availability = null

      this.loadTimes()
    },
    async loadTimes () {
      this.availabilityLoading = true
      const response = await this.$api.booking.lastMinuteAvailability(this.selectedDate.date, this.selectedTime.from, this.selectedTime.to, this.selectedService, (this.voucher && this.voucher.isValid) ? this.voucher : null)
      this.availability = response
      this.availabilityLoading = false

      /* if (this.availability.slots.length == 1) {
        this.slotClicked(this.availability.slots[0])
      } */
    },
    slotClicked (slot) {
      this.$store.dispatch('booking/clearServiceSelections')

      for (let i = 0; i < slot.services.length; i++) {
        const slotService = slot.services[i]

        this.$store.dispatch('booking/setServiceSelected', { service: slotService.service, selected: true, toBack: true })
      }

      this.$store.dispatch('booking/setSelectedSlot', { slot, mode: 'lastminute' })
      const bookingUrlQuery = this.$store.getters['booking/urlQuery']

      this.$router.push(createBookingRoute(bookingUrlQuery, BookingStage.CUSTOMER))
    }
  }
})
</script>
